import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { useState } from "react";
import { zodValidator, fallback } from "@tanstack/zod-adapter";
import { z } from "zod";
import { canonicalUrl, leafCanonicalLink, routeMeta } from "@/lib/seo";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { LetsTalkForm } from "@/components/LetsTalkForm";
import caseStudiesFormImage from "@/assets/case-studies-outdoor-comfort-patio-san-antonio.jpg.asset.json";
import { CASE_STUDIES } from "@/data/caseStudies";
import {
  availableCategories,
  availableCities,
  availableMaterials,
  filterCaseStudies,
  MATERIAL_LABELS,
  type MaterialTag,
} from "@/lib/caseStudyFilters";
import { ArrowRight, MapPin, X } from "lucide-react";
import hero from "@/assets/original/hero-shade.jpg";

const CATEGORIES = [
  "Awnings",
  "Commercial Awnings",
  "Sail Shades",
  "Boat Covers",
  "Stock Trailer Covers",
  "Patio Covers",
] as const;

const MATERIAL_TAGS = [
  "sunbrella",
  "phifertex",
  "vinyl",
  "aluminum",
  "steel",
  "stainless",
] as const;

const searchSchema = z.object({
  city: fallback(z.string().optional(), undefined),
  category: fallback(z.enum(CATEGORIES).optional(), undefined),
  material: fallback(z.enum(MATERIAL_TAGS).optional(), undefined),
});

export const Route = createFileRoute("/case-studies")({
  validateSearch: zodValidator(searchSchema),
  beforeLoad: ({ search }) => {
    if (search.city || search.category || search.material) {
      throw redirect({ to: "/case-studies", search: {}, statusCode: 301 });
    }
  },
  head: (ctx) => {
    const search = (ctx.match.search ?? {}) as z.infer<typeof searchSchema>;
    const parts: string[] = [];
    if (search.city) parts.push(toTitle(search.city));
    if (search.category) parts.push(search.category);
    if (search.material) parts.push(MATERIAL_LABELS[search.material as MaterialTag]);
    const filterText = parts.length ? ` — ${parts.join(" · ")}` : "";
    return {
      // Self-canonical only when /case-studies is the leaf match; child
      // routes (case-studies.$slug, case-studies.in.$city) emit their own.
      links: leafCanonicalLink(ctx, "/case-studies"),
      meta: routeMeta(ctx, {
        title: `Project Case Studies${filterText} | Texas Made Shade`,
        description: `In-depth case studies of Texas Made Shade projects${
          parts.length ? ` filtered by ${parts.join(", ")}` : ""
        } — commercial awnings, sail shade re-covers, marine canvas, stock trailer covers and storefront branding. Pergola covers and welded aluminum frames built in Seguin, TX.`,
        image: hero,
        socialTitle: `Texas Made Shade Case Studies${filterText}`,
        socialDescription:
          "Real installs across Texas: commercial awnings, sail shades, boat covers, stock trailer tops and more.",
        keywords:
          "Texas Made Shade case studies, awning case study, sail shade re-cover, commercial awning project, boat cover project, stock trailer cover project, pergola covers Texas",
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            name: "Texas Made Shade — Project Case Studies",
            itemListElement: CASE_STUDIES.map((c, i) => ({
              "@type": "ListItem",
              position: i + 1,
              url: canonicalUrl(`/case-studies/${c.slug}`),
              name: c.title,
            })),
          }),
        },
      ],
    };
  },
  component: CaseStudiesIndex,
});

function toTitle(slug: string) {
  return slug
    .split("-")
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
    .join(" ");
}

function CaseStudiesIndex() {
  const [search, setSearch] = useState<z.infer<typeof searchSchema>>({});
  const cities = availableCities();
  const categories = availableCategories();
  const materials = availableMaterials();
  const results = filterCaseStudies({
    city: search.city,
    category: search.category,
    material: search.material as MaterialTag | undefined,
  });

  const hasFilter = Boolean(search.city || search.category || search.material);

  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      <main id="main-content" tabIndex={-1}>
        {/* HERO */}
        <section className="relative overflow-hidden bg-navy-deep text-white">
          <div className="absolute inset-0">
            <img src={hero} alt="Texas Made Shade case studies" width={1920} height={1080} loading="eager" decoding="async" fetchPriority="high" className="h-full w-full object-cover opacity-30" />
            <div className="absolute inset-0 bg-gradient-to-r from-navy-deep via-navy-deep/80 to-transparent" />
          </div>
          <div className="relative mx-auto max-w-7xl px-4 py-16 lg:px-8 lg:py-24">
            <div className="inline-flex items-center gap-2 bg-red px-4 py-1.5 font-display text-xs tracking-[0.3em]">
              ★ FIELD REPORTS · TEXAS MADE SHADE
            </div>
            <h1 className="mt-6 max-w-4xl font-display text-4xl font-bold leading-[0.95] sm:text-6xl md:text-7xl">
              Project Case Studies
            </h1>
            <p className="mt-6 max-w-2xl text-lg text-white/85 sm:text-xl">
              Real Texas builds, documented in detail — from commercial storefront awnings and sail shade re-covers to offshore boat covers and ranch trailer tops. Filter by city, project type or material.
            </p>
          </div>
          <div className="stripe-bar absolute bottom-0 left-0 h-2 w-full" />
        </section>

        {/* FILTERS */}
        <section className="border-b-2 border-navy-deep/10 bg-white py-8">
          <div className="mx-auto max-w-7xl px-4 lg:px-8">
            <div className="grid gap-6 lg:grid-cols-3">
              <FilterGroup label="Filter by City">
                {cities.map((c) => (
                  <FilterPill
                    key={c.slug}
                    active={search.city === c.slug}
                    onClick={() => setSearch((prev) => ({
                      ...prev,
                      city: prev.city === c.slug ? undefined : c.slug,
                    }))}
                  >
                    {c.label} <span className="opacity-60">({c.count})</span>
                  </FilterPill>
                ))}
              </FilterGroup>

              <FilterGroup label="Filter by Project Type">
                {categories.map((c) => (
                  <FilterPill
                    key={c.value}
                    active={search.category === c.value}
                    onClick={() => setSearch((prev) => ({
                      ...prev,
                      category:
                        prev.category === c.value
                          ? undefined
                          : (c.value as (typeof CATEGORIES)[number]),
                    }))}
                  >
                    {c.value} <span className="opacity-60">({c.count})</span>
                  </FilterPill>
                ))}
              </FilterGroup>

              <FilterGroup label="Filter by Material">
                {materials.map((m) => (
                  <FilterPill
                    key={m.tag}
                    active={search.material === m.tag}
                    onClick={() => setSearch((prev) => ({
                      ...prev,
                      material: prev.material === m.tag ? undefined : m.tag,
                    }))}
                  >
                    {m.label} <span className="opacity-60">({m.count})</span>
                  </FilterPill>
                ))}
              </FilterGroup>
            </div>

            {hasFilter && (
              <div className="mt-6 flex flex-wrap items-center gap-2 border-t border-navy-deep/10 pt-4">
                <span className="font-display text-xs uppercase tracking-[0.25em] text-navy-deep/60">
                  Active filters:
                </span>
                {search.city && (
                  <ActiveChip
                    label={`City: ${toTitle(search.city)}`}
                    onClick={() => setSearch((prev) => ({ ...prev, city: undefined }))}
                  />
                )}
                {search.category && (
                  <ActiveChip
                    label={`Type: ${search.category}`}
                    onClick={() => setSearch((prev) => ({ ...prev, category: undefined }))}
                  />
                )}
                {search.material && (
                  <ActiveChip
                    label={`Material: ${MATERIAL_LABELS[search.material as MaterialTag]}`}
                    onClick={() => setSearch((prev) => ({ ...prev, material: undefined }))}
                  />
                )}
                <button
                  type="button"
                  onClick={() => setSearch({})}
                  className="ml-auto font-display text-xs uppercase tracking-wider text-red hover:underline"
                >
                  Clear all
                </button>
              </div>
            )}
          </div>
        </section>

        {/* GRID */}
        <section className="bg-cream py-16 lg:py-20">
          <div className="mx-auto max-w-7xl px-4 lg:px-8">
            <div className="mb-6 flex items-end justify-between gap-4">
              <h2 className="font-display text-2xl font-bold uppercase text-navy-deep">
                {results.length} Project{results.length === 1 ? "" : "s"}
              </h2>
            </div>

            {results.length === 0 ? (
              <div className="border-2 border-dashed border-navy-deep/25 bg-white p-10 text-center">
                <p className="font-display text-lg text-navy-deep">
                  No case studies match those filters yet.
                </p>
                <Link
                  to="/case-studies"
                  search={{}}
                  className="mt-4 inline-flex items-center gap-2 bg-red px-5 py-2.5 font-display text-sm font-bold uppercase tracking-wider text-white hover:bg-red/90"
                >
                  Reset filters
                </Link>
              </div>
            ) : (
              <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
                {results.map((c) => (
                  <Link
                    key={c.slug}
                    to="/case-studies/$slug"
                    params={{ slug: c.slug }}
                    className="group flex flex-col overflow-hidden border-2 border-navy-deep/15 bg-white shadow-card transition hover:border-red hover:shadow-bold"
                  >
                    <div className="relative aspect-[4/3] overflow-hidden bg-navy-deep">
                      <img
                        src={c.images[0]?.src}
                        alt={c.images[0]?.alt ?? c.title}
                        width={800}
                        height={600}
                        loading="lazy"
                        decoding="async"
                        className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                      />
                      <span className="absolute left-3 top-3 bg-red px-2 py-0.5 font-display text-[10px] uppercase tracking-[0.2em] text-white">
                        {c.category}
                      </span>
                    </div>
                    <div className="flex flex-1 flex-col gap-3 p-5">
                      <div className="flex items-center gap-1.5 font-display text-[11px] uppercase tracking-[0.25em] text-navy-deep/60">
                        <MapPin className="h-3 w-3 text-red" /> {c.location} · {c.year}
                      </div>
                      <h2 className="font-display text-xl font-bold leading-tight text-navy-deep group-hover:text-red">
                        {c.title}
                      </h2>
                      <p className="text-sm text-navy-deep/75">{c.summary}</p>
                      <div className="mt-auto inline-flex items-center gap-1 font-display text-xs uppercase tracking-[0.2em] text-red">
                        Read the case study <ArrowRight className="h-3 w-3" />
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </section>

        <LetsTalkForm
          imageSrc={caseStudiesFormImage.url}
          imageAlt="Retractable awning over a covered patio with pool and San Antonio skyline at sunset"
        />
      </main>
      <SiteFooter />
    </div>
  );
}

function FilterGroup({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="mb-3 font-display text-xs uppercase tracking-[0.3em] text-navy-deep/60">
        {label}
      </div>
      <div className="flex flex-wrap gap-2">{children}</div>
    </div>
  );
}

function FilterPill({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-block border-2 px-3 py-1.5 font-display text-xs uppercase tracking-wider transition ${
        active
          ? "border-red bg-red text-white"
          : "border-navy-deep/20 bg-white text-navy-deep hover:border-navy-deep"
      }`}
    >
      {children}
    </button>
  );
}

function ActiveChip({
  label,
  onClick,
}: {
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1 border border-navy-deep bg-navy-deep px-2 py-1 font-display text-[11px] uppercase tracking-wider text-white hover:bg-red hover:border-red"
    >
      {label} <X className="h-3 w-3" />
    </button>
  );
}
